export type WorkflowRichTextInlineNode =
  | { type: 'text'; value: string }
  | { type: 'break' }
  | { type: 'strong'; children: WorkflowRichTextInlineNode[] }
  | { type: 'emphasis'; children: WorkflowRichTextInlineNode[] }
  | { type: 'link'; href: string; children: WorkflowRichTextInlineNode[] };

export type WorkflowRichTextBlock =
  | { type: 'paragraph'; children: WorkflowRichTextInlineNode[] }
  | { type: 'heading'; children: WorkflowRichTextInlineNode[] }
  | { type: 'quote'; children: WorkflowRichTextInlineNode[] }
  | { type: 'list'; items: WorkflowRichTextInlineNode[][] };

const unsafeURLCharacters = /[\u0000-\u001F\u007F]/;
const supportedLinkProtocols = new Set(['http:', 'https:', 'mailto:']);

/**
 * 문서 링크에 저장할 수 있는 주소만 정규화한다. 실행 가능한 scheme과 제어 문자는 거부하고,
 * 사용자가 자주 입력하는 www 주소에는 https scheme을 보완한다.
 */
export function normalizeWorkflowRichTextLinkTarget(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed || unsafeURLCharacters.test(trimmed)) return undefined;
  const candidate = /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(candidate)) return undefined;

  try {
    const parsed = new URL(candidate);
    if (!supportedLinkProtocols.has(parsed.protocol)) return undefined;
    if (parsed.protocol === 'mailto:') return parsed.pathname.trim() ? parsed.href : undefined;
    return parsed.hostname ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

export function parseWorkflowRichTextMarkdown(value: string): WorkflowRichTextBlock[] {
  const lines = value.replace(/\r\n?/g, '\n').split('\n');
  const blocks: WorkflowRichTextBlock[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }
    const heading = line.match(/^#{1,3}\s+(.*)$/);
    if (heading) {
      blocks.push({ type: 'heading', children: parseInline(heading[1]) });
      index += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: WorkflowRichTextInlineNode[][] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(parseInline(lines[index].replace(/^\s*[-*]\s+/, '')));
        index += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }
    if (/^>\s?/.test(line)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^>\s?/, ''));
        index += 1;
      }
      blocks.push({ type: 'quote', children: parseInline(quoteLines.join('\n')) });
      continue;
    }
    const paragraphLines = [line];
    index += 1;
    while (index < lines.length && lines[index].trim() && !isBlockOpeningLine(lines[index])) {
      paragraphLines.push(lines[index]);
      index += 1;
    }
    blocks.push({ type: 'paragraph', children: parseInline(paragraphLines.join('\n')) });
  }
  return blocks;
}

export function workflowRichTextHasContent(value: string): boolean {
  const text = parseWorkflowRichTextMarkdown(value)
    .map(blockPlainText)
    .join('');
  return text.trim().length > 0;
}

export function escapeWorkflowRichTextMarkdown(value: string): string {
  return value.replace(/([\\*#>\-[\]()])/g, '\\$1').replaceAll('\u00A0', ' ');
}

export function escapeWorkflowRichTextLinkTarget(value: string): string {
  return value.replace(/([\\()])/g, '\\$1');
}

function isBlockOpeningLine(value: string): boolean {
  return /^#{1,3}\s+/.test(value) || /^\s*[-*]\s+/.test(value) || /^>\s?/.test(value);
}

function parseInline(value: string): WorkflowRichTextInlineNode[] {
  const nodes: WorkflowRichTextInlineNode[] = [];
  const appendText = (text: string) => {
    if (!text) return;
    const previous = nodes[nodes.length - 1];
    if (previous?.type === 'text') previous.value += text;
    else nodes.push({ type: 'text', value: text });
  };
  let index = 0;
  while (index < value.length) {
    if (value[index] === '\\' && index + 1 < value.length) {
      appendText(value[index + 1]);
      index += 2;
      continue;
    }
    if (value[index] === '\n') {
      nodes.push({ type: 'break' });
      index += 1;
      continue;
    }
    if (value.startsWith('**', index)) {
      const closing = findClosingMarker(value, index + 2, '**');
      if (closing >= 0) {
        nodes.push({ type: 'strong', children: parseInline(value.slice(index + 2, closing)) });
        index = closing + 2;
        continue;
      }
    }
    if (value[index] === '*') {
      const closing = findClosingMarker(value, index + 1, '*');
      if (closing >= 0) {
        nodes.push({ type: 'emphasis', children: parseInline(value.slice(index + 1, closing)) });
        index = closing + 1;
        continue;
      }
    }
    if (value[index] === '[') {
      const labelEnd = findClosingMarker(value, index + 1, ']');
      if (labelEnd >= 0 && value[labelEnd + 1] === '(') {
        const targetEnd = findLinkTargetEnd(value, labelEnd + 2);
        if (targetEnd >= 0) {
          const children = parseInline(value.slice(index + 1, labelEnd));
          const target = unescapeMarkdownLinkTarget(value.slice(labelEnd + 2, targetEnd));
          const safeTarget = normalizeWorkflowRichTextLinkTarget(target);
          if (safeTarget && inlinePlainText(children).trim()) nodes.push({ type: 'link', href: safeTarget, children });
          else nodes.push(...children);
          index = targetEnd + 1;
          continue;
        }
      }
    }
    appendText(value[index]);
    index += 1;
  }
  return nodes;
}

function findClosingMarker(value: string, start: number, marker: string): number {
  for (let index = start; index <= value.length - marker.length; index += 1) {
    if (value[index] === '\\') {
      index += 1;
      continue;
    }
    if (value.startsWith(marker, index)) return index;
  }
  return -1;
}

function findLinkTargetEnd(value: string, start: number): number {
  let nestedParentheses = 0;
  for (let index = start; index < value.length; index += 1) {
    if (value[index] === '\\') {
      index += 1;
      continue;
    }
    if (value[index] === '(') nestedParentheses += 1;
    else if (value[index] === ')') {
      if (nestedParentheses === 0) return index;
      nestedParentheses -= 1;
    }
  }
  return -1;
}

function unescapeMarkdownLinkTarget(value: string): string {
  return value.replace(/\\([\\()])/g, '$1');
}

function blockPlainText(block: WorkflowRichTextBlock): string {
  return block.type === 'list' ? block.items.map(inlinePlainText).join('') : inlinePlainText(block.children);
}

function inlinePlainText(nodes: WorkflowRichTextInlineNode[]): string {
  return nodes.map((node) => node.type === 'break' ? '\n' : node.type === 'text' ? node.value : inlinePlainText(node.children)).join('');
}
